import type { ReactNode } from "react";
import { AlertTriangle, ClipboardList, GitCommit, History, PauseCircle, Scale } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CommandCopyBox } from "@/components/common/command-copy-box";
import { cn } from "@/lib/utils";
import type {
  GroomingAreaLevel,
  GroomingBrief,
  GroomingBriefBodyRepo,
  GroomingBriefHistoryEntry,
  GroomingCommentStatus,
  GroomingPlainBrief,
  GroomingSizeDriver,
  WorkItemDetail,
  WorkItemGroomingBriefBlock,
} from "@/lib/api/work-items";

/**
 * The Grooming Brief card on the ADO item page (epic PI-SPEC-SW-E277,
 * feature PI-SPEC-SW-1372).
 *
 * - A brief exists: its sections in the fixed order of the analysis (plain
 *   words first, engineering detail last and collapsed, G4), the size labelled
 *   as an AI suggestion with its range and confidence, a stale banner with the
 *   re-run command, the AI-vs-team line (G10) and the version history (G9).
 * - No brief and the item is ready for grooming: the `/spectree:ado-groom <id>`
 *   copy box. The web never runs the analysis (G1).
 * - No brief and the item is not ready: nothing; the Formulator link on the
 *   page already covers that case.
 * - E279 (PI-SPEC-SW-1387): a held brief shows a "Held: not posted to ADO
 *   yet" chip; a brief with a plain-language block shows it as "Plain
 *   summary" first, for the mixed room. The data comes from the item read
 *   (`item.groomingBrief`, ER-1603 fix B), so the card makes no request.
 *
 * Every brief string is untrusted model output and renders as React text only.
 * PAT errors are handled by the page's AdoPatCallout; when the API could not
 * read the brief store the card is a single muted line that never breaks the page.
 */

const EFFECT_LABELS: Record<GroomingSizeDriver["effect"], string> = {
  "+": "adds size",
  "-": "reduces size",
  uncertainty: "adds uncertainty",
};

const EFFECT_CLASSES: Record<GroomingSizeDriver["effect"], string> = {
  "+": "border-orange-500/30 bg-orange-900/20 text-orange-300",
  "-": "border-green-500/30 bg-green-900/20 text-green-300",
  uncertainty: "border-yellow-500/30 bg-yellow-900/20 text-yellow-300",
};

const COMMENT_STATUS_LABELS: Record<GroomingCommentStatus, string> = {
  pending: "comment pending",
  posted: "comment posted",
  updated: "comment updated",
  reposted: "comment reposted",
  failed: "comment failed",
  held: "held, not posted",
  skipped: "skipped, not posted",
};

const AREA_LEVEL_LABELS: Record<GroomingAreaLevel, string> = {
  main: "main change",
  small: "small change",
  check: "check only",
};

const GENERATOR = "/spectree:ado-groom";

function groomCommand(id: string): string {
  return `${GENERATOR} ${id}`;
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function sizeRange(low: number, high: number): string {
  return `${String(low)}–${String(high)}`;
}

function Section({ title, testId, children }: { title: string; testId: string; children: ReactNode }) {
  return (
    <section className="space-y-2" data-testid={testId}>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {children}
    </section>
  );
}

export function TextList({ items, testId }: { items: string[]; testId?: string }) {
  return (
    <ul className="list-disc space-y-1 pl-5 text-sm" data-testid={testId}>
      {items.map((entry, index) => (
        <li key={index}>{entry}</li>
      ))}
    </ul>
  );
}

export function SizeDrivers({ drivers }: { drivers: GroomingSizeDriver[] }) {
  return (
    <ul className="space-y-2 text-sm">
      {drivers.map((d, index) => (
        <li key={index} className="flex flex-wrap items-start gap-2">
          <span
            className={cn(
              "inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] font-medium",
              EFFECT_CLASSES[d.effect]
            )}
            data-testid="size-driver-effect"
          >
            {EFFECT_LABELS[d.effect]}
          </span>
          <span>
            <span className="font-medium">{d.driver}</span>
            {d.detail && <span className="text-muted-foreground"> — {d.detail}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The engineer-facing notes of a brief (files, evidence, confirmed, inferred,
 * likely cause), without a wrapper. Shared with the E279 item summary, which
 * shows them under "Technical details for the team".
 */
export function EngineerNotesBody({
  brief,
  listTextClassName = "text-xs",
}: {
  brief: GroomingBrief;
  /** Size of the file and evidence lists; the Toolbox reading pane passes its 13 px content size. */
  listTextClassName?: string;
}) {
  const notes = brief.body.engineerNotes;
  const { files, evidence, confirmed, inferred } = notes;

  return (
    <div className="space-y-3" data-testid="brief-engineer-notes-body">
      {files.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs font-medium">Files and areas</h4>
          <ul className={cn("space-y-0.5 font-mono", listTextClassName)}>
            {files.map((f, index) => (
              <li key={index} className="break-all">{f}</li>
            ))}
          </ul>
        </div>
      )}
      {evidence.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs font-medium">Evidence</h4>
          <ul className={cn("space-y-0.5 font-mono", listTextClassName)}>
            {evidence.map((e, index) => (
              <li key={index} className="break-all">{e}</li>
            ))}
          </ul>
        </div>
      )}
      {confirmed.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs font-medium">Confirmed</h4>
          <TextList items={confirmed} />
        </div>
      )}
      {inferred.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs font-medium">Inferred</h4>
          <TextList items={inferred} />
        </div>
      )}
      {notes.bugHypothesis && (
        <div className="space-y-1">
          <h4 className="text-xs font-medium">Likely cause (unverified)</h4>
          <p className="text-sm">{notes.bugHypothesis}</p>
        </div>
      )}
    </div>
  );
}

function EngineerNotes({ brief }: { brief: GroomingBrief }) {
  return (
    <details className="rounded-md border border-white/[0.08] p-3" data-testid="brief-engineer-notes">
      <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        For engineers
      </summary>
      <div className="mt-3">
        <EngineerNotesBody brief={brief} />
      </div>
    </details>
  );
}

function PlainBrief({ plain }: { plain: GroomingPlainBrief }) {
  return (
    <Section title="Plain summary" testId="brief-plain-brief">
      <dl className="space-y-2 text-sm">
        <div>
          <dt className="text-xs font-medium text-muted-foreground">What it is</dt>
          <dd>{plain.whatIsIt}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-muted-foreground">Who notices</dt>
          <dd>{plain.whoNotices}</dd>
        </div>
        {plain.areasTouched.length > 0 && (
          <div>
            <dt className="text-xs font-medium text-muted-foreground">Areas touched</dt>
            <dd>
              <ul className="space-y-1" data-testid="brief-plain-areas">
                {plain.areasTouched.map((a, index) => (
                  <li key={index}>
                    <span className="font-medium">{a.area}</span>{" "}
                    <span className="text-muted-foreground">({AREA_LEVEL_LABELS[a.level]})</span> — {a.what}
                  </li>
                ))}
              </ul>
            </dd>
          </div>
        )}
        <div>
          <dt className="text-xs font-medium text-muted-foreground">Size in words</dt>
          <dd>{plain.sizeInWords}</dd>
        </div>
        {plain.comparableInWords && (
          <div>
            <dt className="text-xs font-medium text-muted-foreground">Like earlier work</dt>
            <dd>{plain.comparableInWords}</dd>
          </div>
        )}
        {plain.worthKnowing.length > 0 && (
          <div>
            <dt className="text-xs font-medium text-muted-foreground">Worth knowing</dt>
            <dd>
              <TextList items={plain.worthKnowing} />
            </dd>
          </div>
        )}
        {plain.askTheTeam.length > 0 && (
          <div>
            <dt className="text-xs font-medium text-muted-foreground">Ask the team</dt>
            <dd>
              <TextList items={plain.askTheTeam} />
            </dd>
          </div>
        )}
      </dl>
    </Section>
  );
}

interface FooterRepo {
  name: string;
  commit: string | undefined;
  examined: boolean;
}

/** Stored repositories, falling back to the body's when the stored row lacks a field. */
function footerRepos(brief: GroomingBrief): FooterRepo[] {
  const bodyRepos: GroomingBriefBodyRepo[] = brief.body.repos;
  if (brief.repos.length === 0) {
    return bodyRepos.map((r) => ({ name: r.name, commit: r.commit, examined: r.examined }));
  }
  return brief.repos.map((r) => {
    const fromBody = bodyRepos.find((b) => b.name === r.repo);
    return {
      name: r.repo,
      commit: r.commit ?? fromBody?.commit,
      examined: r.examined ?? fromBody?.examined ?? true,
    };
  });
}

function HistoryList({ history }: { history: GroomingBriefHistoryEntry[] }) {
  return (
    <details className="rounded-md border border-white/[0.08] p-3" data-testid="brief-history">
      <summary className="flex cursor-pointer items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <History className="h-3.5 w-3.5" />
        History ({history.length} {history.length === 1 ? "version" : "versions"})
      </summary>
      <ul className="mt-3 space-y-1 text-xs">
        {history.map((entry) => (
          <li key={entry.id} className="flex flex-wrap gap-x-3 gap-y-0.5" data-testid="brief-history-entry">
            <span>{formatDate(entry.createdAt)}</span>
            <span className="text-muted-foreground">{entry.createdBy?.name ?? "Unknown author"}</span>
            <span>
              {entry.suggestedPoints} ({sizeRange(entry.pointsLow, entry.pointsHigh)})
            </span>
            <span className={cn(entry.commentStatus === "failed" ? "text-yellow-400" : "text-muted-foreground")}>
              {COMMENT_STATUS_LABELS[entry.commentStatus]}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}

function BriefCard({ item, data, brief }: { item: WorkItemDetail; data: WorkItemGroomingBriefBlock; brief: GroomingBrief }) {
  const body = brief.body;
  const { whatChanges, sizeDrivers, comparables, questions } = body;
  const repos = footerRepos(brief);
  const range = sizeRange(brief.pointsLow, brief.pointsHigh);

  return (
    <Card className="glass-card" data-testid="grooming-brief-panel">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <ClipboardList className="h-4 w-4" />
          Grooming brief
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {data.stale && (
          <div
            className="space-y-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3"
            role="status"
            data-testid="brief-stale-banner"
          >
            <p className="flex items-start gap-2 text-sm text-amber-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              This item changed since the brief was written. Re-run in Claude Code:
            </p>
            <CommandCopyBox
              label="Re-run the grooming brief"
              command={groomCommand(item.ref.id)}
              helperText="Run this in Claude Code inside the code repository. It writes a new version and edits the earlier ADO comment in place."
            />
          </div>
        )}

        {brief.commentStatus === "held" && (
          <p
            className="inline-flex items-center gap-1.5 rounded-full border border-sky-500/30 bg-sky-900/20 px-2.5 py-0.5 text-xs font-medium text-sky-300"
            data-testid="brief-held-chip"
          >
            <PauseCircle className="h-3.5 w-3.5" />
            Held: not posted to ADO yet
          </p>
        )}

        {brief.commentStatus === "failed" && (
          <p className="flex items-center gap-2 text-xs text-yellow-400" data-testid="brief-comment-failed">
            <AlertTriangle className="h-3.5 w-3.5" />
            The ADO comment was not posted. The brief is saved here.
          </p>
        )}

        <div className="space-y-1" data-testid="brief-size-line">
          <p className="flex flex-wrap items-center gap-2 text-sm">
            <Scale className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">AI suggestion — the team decides:</span>
            <span>
              {brief.suggestedPoints} (could be {range}) · confidence {brief.confidence}
            </span>
          </p>
          {data.teamPoints !== null ? (
            <p className="text-sm text-muted-foreground" data-testid="brief-team-points">
              AI suggested {brief.suggestedPoints} ({range}) · team set {data.teamPoints}
            </p>
          ) : (
            brief.estimateAtSave != null && (
              <p className="text-sm text-muted-foreground" data-testid="brief-team-points">
                The team had already set {brief.estimateAtSave} before this brief.
              </p>
            )
          )}
        </div>

        {body.plainBrief && <PlainBrief plain={body.plainBrief} />}

        <Section title="In plain words" testId="brief-plain-summary">
          <p className="whitespace-pre-line text-sm">{body.plainSummary}</p>
        </Section>

        <Section title="What has to change" testId="brief-what-changes">
          <TextList items={whatChanges} />
        </Section>

        <Section title="Why this size" testId="brief-size-drivers">
          <SizeDrivers drivers={sizeDrivers} />
        </Section>

        <Section title="Suggested size" testId="brief-suggested-size">
          <p className="text-sm">
            {brief.suggestedPoints} points, could be {range}. Confidence {brief.confidence}.
          </p>
          {body.splitSuggestion && (
            <p className="text-sm">
              <span className="font-medium">Consider splitting:</span> {body.splitSuggestion}
            </p>
          )}
        </Section>

        <Section title="Similar past items" testId="brief-comparables">
          {brief.calibrated && comparables.length > 0 ? (
            <ul className="space-y-1 text-sm">
              {comparables.map((c) => (
                <li key={c.id}>
                  <span className="font-mono text-xs text-muted-foreground">#{c.id}</span>{" "}
                  <span className="font-medium">{c.title}</span>{" "}
                  <span className="text-muted-foreground">({c.points} pts)</span> — {c.why}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm italic text-muted-foreground">
              Uncalibrated: no comparable past items with points were available.
            </p>
          )}
        </Section>

        <Section title="Questions for grooming" testId="brief-questions">
          {questions.length > 0 ? (
            <TextList items={questions} />
          ) : (
            <p className="text-sm italic text-muted-foreground">No open questions.</p>
          )}
        </Section>

        <EngineerNotes brief={brief} />

        <footer
          className="space-y-1 border-t border-white/[0.08] pt-3 text-xs text-muted-foreground"
          data-testid="brief-footer"
        >
          {repos.length > 0 && (
            <ul className="space-y-0.5">
              {repos.map((r, index) => (
                <li key={index} className="flex flex-wrap items-center gap-1">
                  <GitCommit className="h-3 w-3" />
                  <span className="font-medium text-foreground">{r.name}</span>
                  {r.commit && <span className="font-mono">@ {r.commit.slice(0, 12)}</span>}
                  {!r.examined && <span className="text-yellow-400">(not examined)</span>}
                </li>
              ))}
            </ul>
          )}
          <p>
            Written {formatDate(brief.createdAt)}
            {brief.createdBy?.name ? ` by ${brief.createdBy.name}` : ""} · generated by {GENERATOR}
          </p>
          {brief.postedAt && (
            <p data-testid="brief-posted-by">
              Posted to ADO {formatDate(brief.postedAt)}
              {brief.postedBy?.name ? ` by ${brief.postedBy.name}` : ""}
            </p>
          )}
        </footer>

        {data.history.length > 0 && <HistoryList history={data.history} />}
      </CardContent>
    </Card>
  );
}

export function GroomingBriefPanel({ item }: { item: WorkItemDetail }) {
  const data = item.groomingBrief;

  if (!data) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="grooming-brief-error">
        The grooming brief could not be loaded.
      </p>
    );
  }

  if (data.brief) {
    return <BriefCard item={item} data={data} brief={data.brief} />;
  }

  if (!item.readiness.readyForGrooming) return null;

  return (
    <Card className="glass-card" data-testid="grooming-brief-panel">
      <CardContent className="pt-6">
        <CommandCopyBox
          label="Write a grooming brief"
          command={groomCommand(item.ref.id)}
          helperText="Run this in Claude Code inside the code repository to write a grooming brief."
        />
      </CardContent>
    </Card>
  );
}
